import { trpc } from "@/api/trpc";
import { cn } from "@/shared/lib/utils";
import { ROLE_LABELS_CS, type UserRole } from "@template/shared";
import { forwardRef } from "react";

type NativeSelectProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "children">;

interface UserSelectProps extends NativeSelectProps {
  /** Volitelně omez nabídku jen na vybrané role (např. obchodník, ops, admin). */
  roles?: readonly UserRole[];
  /** Popisek první (prázdné = nepřiřazeno) možnosti. */
  placeholder?: string;
}

/**
 * Dropdown pro výběr interního uživatele (obchodník / správce / lektor).
 * Plněno z `hca.users.assignable` — nahrazuje ruční zadávání opaque user-ID.
 * Hodnota = user.id, popisek = jméno (e-mail) · role.
 */
export const UserSelect = forwardRef<HTMLSelectElement, UserSelectProps>(
  ({ roles, placeholder = "— nepřiřazeno —", className, ...props }, ref) => {
    const { data: users = [], isPending } = trpc.hca.users.assignable.useQuery();
    // Filtrujeme přes UNION primární `role` + doplňkových `roles`, aby šel
    // přiřadit i multi-rolový člověk (např. primárně obchodník + doplňkově lektor).
    const options = roles
      ? users.filter((u) => roles.includes(u.role) || u.roles.some((r) => roles.includes(r)))
      : users;

    return (
      <select
        ref={ref}
        className={cn(
          "h-9 w-full rounded-md border border-input bg-background px-2 text-sm",
          className,
        )}
        {...props}
      >
        <option value="">{isPending ? "Načítám…" : placeholder}</option>
        {options.map((u) => {
          // Zobraz všechny role (primární + doplňkové), ať admin vidí, proč je
          // člověk v nabídce (multi-rolový lektor apod.).
          const roleLabels = [u.role, ...u.roles]
            .filter((r, i, arr) => arr.indexOf(r) === i)
            .map((r) => ROLE_LABELS_CS[r])
            .join(" + ");
          return (
            <option key={u.id} value={u.id}>
              {u.name || u.email}
              {roleLabels ? ` · ${roleLabels}` : ""}
            </option>
          );
        })}
      </select>
    );
  },
);

UserSelect.displayName = "UserSelect";
